// "Bussen (bpost)": the letterboxes bpost serves for a CONNECT (no-sticker) distribution.
//
// bpost's figure for a selection is the TotalNumberOfBoxes of one calculateNumberOfBoxes call over
// the whole code list — exactly what an order is billed on (checked against 21 live Distritool
// orders on 2026-11-03). Per-code figures overlap and are never added up, so the selection is sent
// as one list: fully selected sub-municipalities as their NIS6 code, the rest as sectors. bpost
// renumbered merged municipalities but kept the old NIS6/NIS9 codes the GIS layer uses, so no
// municipality codes are sent. "In" and "after segmentation" are two lists, each cached by content.
import { state } from './state.js';
import { GEOSERVER_URL } from '../config.js';

const SECTOR_TYPENAME = 'geolinq:u101413ViewGroupAdresType1';
const universe = new Map(); // nis5 -> Map(nis6 -> Set(sector codes)); undefined = in flight
const totals = new Map();   // list signature -> number | null (failed); undefined = in flight
const unknownCodes = new Set();
const queued = new Map();   // list signature -> codes
let pending = 0, timer = null, onUpdate = null;
let last = { all: 0, kept: 0, dropped: new Set() };

export function initBpostBoxes(cb) { onUpdate = cb; }
export function bpostLoading() { return pending > 0 || queued.size > 0; }

const nis5Of = (code) => (/^[0-9]{5}/.test(String(code)) ? String(code).slice(0, 5) : null);
// "31005A342" -> "31005A"; codes without a letter at position 6 (harbour areas "3100514MQ") → null.
export const nis6Of = (code) => (/^[0-9]{5}[A-Z]/.test(String(code)) ? String(code).slice(0, 6) : null);
const isUnit = (k) => !String(k).startsWith('PC');

// All sector codes of a municipality, grouped per sub-municipality (one light WFS query, no geometry).
async function fetchUniverse(nis5) {
  const url = `${GEOSERVER_URL}?service=WFS&version=1.0.0&request=GetFeature&typename=${SECTOR_TYPENAME}` +
    `&outputFormat=application/json&propertyName=u108267Admin_NL&maxFeatures=5000` +
    `&CQL_FILTER=${encodeURIComponent(`u108267Admin_NL LIKE '${nis5}%'`)}`;
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`WFS ${resp.status}`);
  const data = await resp.json();
  const per6 = new Map();
  (data.features || []).forEach((f) => {
    const code = String(f.properties?.u108267Admin_NL || '').replace(/-+$/, '');
    const d = nis6Of(code); if (!d) return;
    if (!per6.has(d)) per6.set(d, new Set());
    per6.get(d).add(code);
  });
  universe.set(nis5, per6);
}

// Sector keys → the shortest code list bpost accepts for them. null while a municipality's
// sector list is still loading.
function compress(keys) {
  const by6 = new Map(), loose = [];
  for (const k of keys) {
    const m = nis5Of(k); if (!m) continue;
    const u = universe.get(m);
    if (u === undefined) return null;
    const d = nis6Of(k);
    if (!d || !u.has(d)) { loose.push(k); continue; }
    if (!by6.has(d)) by6.set(d, []);
    by6.get(d).push(k);
  }
  const out = [...loose];
  by6.forEach((list, d) => {
    const all = universe.get(d.slice(0, 5)).get(d);
    if (list.length >= all.size && [...all].every((c) => list.includes(c))) out.push(d);
    else out.push(...list);
  });
  return out.sort();
}

async function flush() {
  timer = null;
  const batch = [...queued.entries()];
  queued.clear();
  if (!batch.length) return;
  pending++;
  try {
    // The Function takes up to 20 lists per call; a few calls in parallel.
    const parts = [];
    for (let i = 0; i < batch.length; i += 20) parts.push(batch.slice(i, i + 20));
    await Promise.all(parts.map(async (part) => {
      const lists = {};
      part.forEach(([, codes], j) => { lists[`l${j}`] = codes; });
      try {
        const resp = await fetch('/api/bpost/total', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ lists, product: 'CONNECT' }) });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.message || data.error || `HTTP ${resp.status}`);
        (data.unknown || []).forEach((c) => unknownCodes.add(String(c).replace(/-+$/, '')));
        part.forEach(([sig], j) => totals.set(sig, Number(data.totals?.[`l${j}`] ?? 0)));
      } catch (e) {
        console.warn('bpost total failed', e.message);
        part.forEach(([sig]) => totals.set(sig, null));
      }
    }));
  } finally {
    pending--;
    if (onUpdate) onUpdate();
  }
}

// Total for a code list: the cached value, or undefined after queueing a (debounced) request.
function totalFor(codes) {
  if (!codes.length) return 0;
  const sig = codes.join(',');
  if (totals.has(sig)) return totals.get(sig);
  totals.set(sig, undefined);
  queued.set(sig, codes);
  clearTimeout(timer);
  timer = setTimeout(flush, 250); // dragging the threshold fires many lists; ask for the last ones
  return undefined;
}

// bpost total for any group of sector keys (a table row): number, undefined while loading, null on
// failure, '' when the group has no sector codes (postcode units).
export function bpostTotalOf(keys) {
  const units = keys.filter(isUnit);
  if (!units.length) return '';
  const c = compress(units);
  return c ? totalFor(c) : undefined;
}

// Load the sector lists of every municipality the selection touches. Safe to call on every change.
export async function ensureBpostBoxes() {
  const need = new Set();
  state.sectors.forEach((s, k) => { if (!isUnit(k)) return; const m = nis5Of(k); if (m && !universe.has(m)) need.add(m); });
  if (!need.size) { if (onUpdate) onUpdate(); return; }
  const list = [...need];
  list.forEach((m) => universe.set(m, undefined));
  pending++;
  try {
    let idx = 0;
    const worker = async () => { while (idx < list.length) { const m = list[idx++]; try { await fetchUniverse(m); } catch (e) { console.warn('WFS municipality failed', m, e.message); universe.set(m, new Map()); } } };
    await Promise.all(Array.from({ length: Math.min(6, list.length) }, worker));
  } finally {
    pending--;
    if (onUpdate) onUpdate();
  }
}

// Panel figures. missing > 0 while a list is loading; failed = bpost couldn't answer.
export function bpostSums(dropped) {
  const sel = [], kept = [];
  state.sectors.forEach((s, k) => { if (!isUnit(k)) return; sel.push(k); if (!dropped.has(k)) kept.push(k); });
  const res = { all: 0, kept: 0, missing: 0, failed: 0, unknown: 0, exact: true };
  if (!sel.length) return res;
  res.unknown = sel.filter((k) => unknownCodes.has(k)).length;
  const cIn = compress(sel), cKept = compress(kept);
  if (!cIn || !cKept) { res.missing = 1; return res; }
  const vIn = totalFor(cIn), vKept = kept.length === sel.length ? vIn : totalFor(cKept);
  for (const v of [vIn, vKept]) { if (v === undefined) res.missing++; else if (v === null) res.failed++; }
  res.all = vIn || 0; res.kept = vKept || 0;
  if (!res.missing && !res.failed) last = { all: res.all, kept: res.kept, dropped: new Set(dropped) };
  return res;
}

// Per-sector breakdown for the export tables only: the selection's bpost total spread over its
// sectors by GIS address share (bpost itself has no additive per-sector figure). Sums match the panel.
export function bpostPerSector() {
  const out = new Map();
  let hhKept = 0, hhDrop = 0;
  state.sectors.forEach((s, k) => { if (!isUnit(k)) return; if (last.dropped.has(k)) hhDrop += s.totalHH || 0; else hhKept += s.totalHH || 0; });
  state.sectors.forEach((s, k) => {
    if (!isUnit(k)) return;
    const hh = s.totalHH || 0;
    if (last.dropped.has(k)) out.set(k, hhDrop ? (last.all - last.kept) * hh / hhDrop : 0);
    else out.set(k, hhKept ? last.kept * hh / hhKept : 0);
  });
  return out;
}
